import {emptyObject} from '@packages/utilities/fp/utils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {
  Plugins,
  Component,
  type StyleAttributeObject,
  type AttributesObject,
} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  EList,
  ELiteral,
  EBoolean,
  ERecord,
  EText,
  TTypeReference,
  type Expression,
} from '@packages/systems/wfdl-ast';

import type {PageConstraint} from '@packages/systems/constraints-types';
import type {SharedContextType} from '@packages/systems/core/records/SharedContext';

/**
 * This is specifically for restricting atoms from existing outside of
 * utility pages and will probably be removed in the future if we allow for user atoms to be used on other pages.
 * This duplicates similar functionality provided by
 * the `pages` package. That being said, it needs to exist
 * within this package (`plugins`) to prevent a circular dependency.
 */
export const UsysUtilityPageConstraint: PageConstraint = {
  type: 'UtilityPage',
  is: [
    'usys-log-in',
    'usys-sign-up',
    'usys-reset-password',
    'usys-update-password',
    'usys-user-account',
  ],
};

export const setDisplayBlock = (style: StyleAttributeObject) =>
  style.display === 'block' ? style : {...style, display: 'block'};

export const setDisplayNone = (style: StyleAttributeObject | undefined) => {
  if (style === undefined) {
    return {display: 'none'};
  } else if (style.display === 'none') {
    return style;
  } else {
    return {
      ...style,
      display: 'none',
    };
  }
};

// returns props to hide atom on a published site so that site module can control visibility
export const hideAtomByDefault = (
  sharedContext: SharedContextType,
  shouldShowInDesigner: boolean = true,
  runtimeEnvironment: Environment
) => {
  let shouldHide = true;

  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
  if (isDesigner && shouldShowInDesigner) {
    shouldHide = false;
  }
  return (props: AttributesObject): AttributesObject => {
    if (!shouldHide) return props;
    return {
      ...props,
      style: setDisplayNone(props.style || emptyObject),
    };
  };
};

export const extraAtomFields = TTypeReference('extraAtomFields');
export const extraAttrs = TTypeReference('extraAttrs');

// error utils

export const getErrorAttrName = (errorAttr: string, errorCode: string) => {
  const formattedErrorCode = errorCode.replace('_', '-').toLowerCase();
  return `${errorAttr}-${formattedErrorCode}-error`;
};

// @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'text' implicitly has an 'any' type.
const link = ({id, text}) =>
  EElement({
    id,
    type: qualify(Plugins.name('Basic'), Component.name('Link')),
    data: ERecord({
      link: ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('Link')),
        value: {mode: 'external', url: '#'},
      }),
      button: EBoolean(false),
      block: EText('inline'),
      eventIds: EList([]),
      children: EList([
        EElement({
          id: `${id} String`,
          type: qualify(Plugins.name('Basic'), Component.name('String')),
          data: EText(text),
        }),
      ]),
    }),
  });

export const privacyText: Expression = EList([
  EElement({
    id: `By Creating String`,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText("By creating an account, I agree to this website's "),
  }),
  link({id: 'Privacy Policy Link', text: 'privacy policy'}),
  EElement({
    id: `And String`,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(' and '),
  }),
  link({id: 'Terms Of Service Link', text: 'terms of service'}),
]);

export const communicationsText: Expression = EList([
  EElement({
    id: `By Creating String`,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText('I consent to receive marketing emails.'),
  }),
]);
